import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSpace } from "../../contexts/SpaceContext";
import { CircularProgress, Box } from "@mui/material";

/**
 * SpaceGuard ensures that all space-aware routes have a spaceId
 * If no spaceId is in the URL but one is available in context, it redirects to add the spaceId
 */
const SpaceGuard = ({ children }) => {
  const { currentSpace, loading } = useSpace();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip if still loading spaces
    if (loading) return;

    // If we have a current space
    if (currentSpace?.id) {
      const params = new URLSearchParams(location.search);
      const urlSpaceId = params.get("spaceId");

      // If URL doesn't have spaceId or has a different one than current
      if (!urlSpaceId || urlSpaceId !== currentSpace.id) {
        // Add the current spaceId to the URL and redirect
        params.set("spaceId", currentSpace.id);
        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          { replace: true }
        );
      }
    }
  }, [currentSpace, location, navigate, loading]);

  // Show loading indicator while checking/redirecting
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100px",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Once we've ensured the spaceId is in the URL, render the children
  return children;
};

export default SpaceGuard;
